/**
 * Arthaus Discovery Re-rank
 * Phase: 9b (chips) + 9a (sidebar "Refine in words")
 *
 * Sorts the product grid toward a graph vibe without a page load:
 *   • a "rerank" chip (9b) or the sidebar Refine box (9a) → POST /api/rerank →
 *     re-render [data-product-grid] with the ranked pieces + a "Sorted by X ✕" pill.
 *   • Clear restores the original server grid.
 *   • 9b live fallback: if no chips were precomputed, fetch /api/themes and render them.
 * Progressive enhancement — the native grid, filters and pagination work without this.
 */
(function () {
  'use strict';

  function esc(s) {
    return (s == null ? '' : String(s)).replace(/&/g, '&amp;').replace(/</g, '&lt;')
      .replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  // Fallback when discovery-grid.js isn't on the page — same shared card markup (assets/arthaus-card.js).
  function gridCard(p) {
    return window.ArthausCard ? window.ArthausCard.render(p) : '';
  }

  function Rerank(chipsRoot, refineRoot) {
    this.chipsRoot = chipsRoot || null;
    this.refineRoot = refineRoot || null;
    var cfg = chipsRoot || refineRoot;
    this.api = (cfg.getAttribute('data-api') || '').replace(/\/$/, '');
    this.conceptKey = cfg.getAttribute('data-concept-key') || '';
    this.collectionHandle = cfg.getAttribute('data-collection-handle') || '';
    this.allHandles = null;                                            // full collection pool
    this.hideArtist = cfg.getAttribute('data-hide-artist') === 'true'; // single-artist pages
    this.mlt = cfg.getAttribute('data-mlt') !== 'false';               // off where no walk drawer
    this.limit = parseInt(cfg.getAttribute('data-rerank-limit'), 10) || 24;
    this.grid = document.querySelector('[data-product-grid]');
    this.originalGrid = this.grid ? this.grid.innerHTML : '';
    this.activeChip = null;
    this.bind();
    var self = this;
    this.ensureHandles().then(function () {
      if (chipsRoot && !chipsRoot.querySelector('.chip-link, .chip-rerank')) self.liveThemes();
    });
  }

  // Resolve the full pool once: concept_key for graph concepts / artists (via BY→Artist),
  // else the collection's whole product-handle list (regular "room-spill" collections).
  Rerank.prototype.ensureHandles = function () {
    var self = this;
    if (this.conceptKey || this.allHandles) return Promise.resolve(this.allHandles);
    if (window.ArthausGrid && window.ArthausGrid.collectionHandles && this.collectionHandle) {
      return window.ArthausGrid.collectionHandles(this.collectionHandle)
        .then(function (list) { self.allHandles = list && list.length ? list : null; return self.allHandles; });
    }
    return Promise.resolve(null);
  };

  // Pool to rerank: concept_key when the collection maps to a graph concept/artist, the full
  // collection handles when we fetched them, else the visible page's handles as a fallback.
  Rerank.prototype.poolBody = function () {
    if (this.conceptKey) return { concept_key: this.conceptKey };
    if (this.allHandles && this.allHandles.length) return { handles: this.allHandles };
    var handles = Array.prototype.slice
      .call(document.querySelectorAll('[data-product-grid] a[href*="/products/"]'))
      .map(function (a) { return (a.getAttribute('href').split('/products/')[1] || '').split('?')[0]; })
      .filter(Boolean);
    return { handles: handles.filter(function (h, i) { return handles.indexOf(h) === i; }) };
  };

  Rerank.prototype.bind = function () {
    var self = this;
    if (this.chipsRoot) {
      this.chipsRoot.addEventListener('click', function (e) {
        var chip = e.target.closest && e.target.closest('.chip-rerank');
        if (chip) { e.preventDefault(); self.onChip(chip); }
      });
    }
    if (this.refineRoot) {
      var input = this.refineRoot.querySelector('[data-refine-input]');
      var go = this.refineRoot.querySelector('[data-refine-go]');
      var fire = function () { var v = (input.value || '').trim(); if (v) self.toward(v, v, self.refineRoot); };
      if (go) go.addEventListener('click', fire);
      if (input) input.addEventListener('keydown', function (e) { if (e.key === 'Enter') { e.preventDefault(); fire(); } });
    }
    // Clear buttons live inside each pill (chips section and/or refine box).
    document.querySelectorAll('[data-rerank-clear]').forEach(function (b) {
      b.addEventListener('click', function () { self.clear(); });
    });
  };

  Rerank.prototype.onChip = function (chip) {
    if (this.activeChip === chip) { this.clear(); return; }
    this.setActiveChip(chip);
    this.toward(chip.getAttribute('data-rerank-seed'), chip.getAttribute('data-label') || chip.getAttribute('data-rerank-seed'), this.chipsRoot);
  };

  Rerank.prototype.toward = function (seed, label, scope) {
    if (!this.grid || !seed) return;
    var self = this;
    var body = this.poolBody();
    body.toward = seed;
    body.limit = this.limit;
    if (window.ArthausGrid) window.ArthausGrid.beginLoad(this.grid, this.limit);
    else this.grid.style.opacity = '0.5';

    fetch(this.api + '/api/rerank', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-Arthaus-Client': 'storefront' },
      body: JSON.stringify(body)
    })
      .then(function (r) { if (!r.ok) throw new Error('rerank ' + r.status); return r.json(); })
      .then(function (d) {
        if (!(d.products || []).length) { self.clear(); return; }
        if (window.ArthausGrid) window.ArthausGrid.render(self.grid, d.products, { mlt: self.mlt, hideArtist: self.hideArtist });
        else { self.grid.style.opacity = ''; self.grid.innerHTML = (d.products || []).map(gridCard).filter(Boolean).join(''); }
        self.showPill(scope, label);
      })
      .catch(function () { if (window.ArthausGrid) window.ArthausGrid.endLoad(self.grid); else self.grid.style.opacity = ''; self.clear(); });
  };

  Rerank.prototype.showPill = function (scope, label) {
    // hide any other pills, show the one nearest this trigger
    document.querySelectorAll('[data-rerank-active]').forEach(function (b) { b.classList.add('hidden'); });
    var box = (scope && scope.querySelector('[data-rerank-active]')) || document.querySelector('[data-rerank-active]');
    if (box) {
      var lbl = box.querySelector('[data-rerank-active-label]');
      if (lbl) lbl.textContent = label;
      box.classList.remove('hidden');
    }
  };

  Rerank.prototype.setActiveChip = function (chip) {
    if (this.activeChip) this.activeChip.classList.remove('!border-bronze', '!text-bronze', 'bg-parchment');
    this.activeChip = chip;
    chip.classList.add('!border-bronze', '!text-bronze', 'bg-parchment');
  };

  Rerank.prototype.clear = function () {
    if (this.activeChip) { this.activeChip.classList.remove('!border-bronze', '!text-bronze', 'bg-parchment'); this.activeChip = null; }
    if (this.grid) this.grid.innerHTML = this.originalGrid;
    document.querySelectorAll('[data-rerank-active]').forEach(function (b) { b.classList.add('hidden'); });
  };

  // 9b live fallback: build rerank-only chips when nothing was precomputed.
  Rerank.prototype.liveThemes = function () {
    var self = this;
    var body = this.poolBody();
    body.count = 8;
    fetch(this.api + '/api/themes', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-Arthaus-Client': 'storefront' },
      body: JSON.stringify(body)
    })
      .then(function (r) { if (!r.ok) throw new Error('themes ' + r.status); return r.json(); })
      .then(function (d) {
        var chips = (d.themes || []).map(function (t) {
          return '<button type="button" class="chip-rerank snap-start shrink-0 inline-flex items-center gap-1.5 rounded-full border border-border bg-white px-4 py-2 font-body text-sm text-charcoal hover:border-bronze hover:text-bronze transition-colors" ' +
            'data-rerank-seed="' + esc(t.rerank_seed) + '" data-label="' + esc(t.label) + '" title="' + esc(t.blurb) + '">' +
            '<span class="text-bronze">✦</span>' + esc(t.label) + '</button>';
        }).join('');
        if (chips) self.chipsRoot.innerHTML = chips;
      })
      .catch(function () { /* leave rail empty — grid unaffected */ });
  };

  function init() {
    var chips = document.querySelector('[data-concept-chips]');
    var refine = document.querySelector('[data-refine]');
    if (!chips && !refine) return;
    if (window.__arthausRerank) return; // one controller per page
    window.__arthausRerank = new Rerank(chips, refine);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
